<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :disable="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div>
        <label for="description" class="text-sm font-medium">{{
          $t('qst-actions.description')
        }}</label>
        <BaseInput
          v-model="modifiedData.description"
          :disabled="!modifiedData.isFromManager"
          color="base"
          show-error-as-tooltip
          :error="$v.description.required.$invalid ? $v.description.required.$message : ''" />
      </div>
      <div>
        <label for="sequence" class="text-sm font-medium">{{ $t('material.sequence') }}</label>
        <BaseInput
          v-model="modifiedData.sequence"
          :disabled="!modifiedData.isFromManager"
          color="base"
          show-error-as-tooltip
          :error="$v.sequence.required.$invalid ? $v.sequence.required.$message : ''" />
      </div>

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive"
        :disable="!modifiedData.isFromManager" />
      <BaseToggle
        :label="$t('qst-actions.settled')"
        name="isSettled"
        v-model="modifiedData.isSettled"
        :disable="!modifiedData.isFromManager" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { type QSTActionDetail } from '@/types/QSTActionDetails';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    actionDetail: QSTActionDetail;
  }>(),
  {}
);

const modifiedData = ref<QSTActionDetail>(deepCopy<QSTActionDetail>(props.actionDetail));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.description = newData.description || '';
    validationModel.value.sequence = newData.sequence || '';
    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.actionDetail);
    isModified.value = valueChanged;
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<QSTActionDetail>(props.actionDetail);
  emit('closeModal');
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

const validationModel = ref({
  description: modifiedData.value.description || '',
  sequence: modifiedData.value.sequence || '',
});

const rules = computed(() => ({
  description: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  sequence: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
